import { useMemo } from "react";
import { useQuery } from "react-query";

import { xdaProjectionDimensionsAtom } from "atoms";
import { AxiosError } from "axios";
import { useAtomValue } from "jotai";

import {
  ENTITIES_ON_MAP_QUERY_KEY,
  ENTITIES_ON_MAP_STALE_TIME,
  MAP_QUERY_CACHE_TIME,
  XdaProjectionColorEntry,
  XdaProjectionRequest,
  XdaProjectionResponse,
  fetchEntitiesOnMap,
  fetchXdaProjection
} from "api/map";

export const GetXdaProjectionKey = "xdaProjection";

const emptyXdaProjectionResponse: XdaProjectionResponse = {
  type: "FeatureCollection",
  features: []
};

interface XdaProjectionOptions {
  showXdaProjection: boolean;
  txnId: Maybe<string>;
  canFetch: boolean;
}

const useGetXdaProjection = (options: XdaProjectionOptions) => {
  const { showXdaProjection, txnId, canFetch } = options;

  const xdaDimensions = useAtomValue(xdaProjectionDimensionsAtom);

  const {
    data: entitiesData,
    isLoading: isLoadingEntities,
    isFetching: isFetchingEntities
  } = useQuery(
    [ENTITIES_ON_MAP_QUERY_KEY, txnId],
    async () => {
      if (!txnId) return null;
      return await fetchEntitiesOnMap(txnId);
    },
    {
      enabled: showXdaProjection && !!txnId && canFetch,
      staleTime: ENTITIES_ON_MAP_STALE_TIME,
      cacheTime: MAP_QUERY_CACHE_TIME
    }
  );

  const { uwis, colorMap } = useMemo(() => {
    if (!entitiesData) {
      return { uwis: [], colorMap: [] };
    }

    const allUwis: string[] = [];
    const allColorMap: XdaProjectionColorEntry[] = [];

    Object.keys(entitiesData).forEach((groupKey) => {
      const group = entitiesData[groupKey];
      Object.keys(group.uwis).forEach((uwi) => {
        allUwis.push(uwi);
        allColorMap.push({
          uwi,
          color: group.uwis[uwi], // color is stored as the value
          group: group.group?.title || ""
        });
      });
    });

    return { uwis: allUwis, colorMap: allColorMap };
  }, [entitiesData]);

  const shouldFetch =
    showXdaProjection &&
    canFetch &&
    uwis.length > 0 &&
    !isLoadingEntities &&
    !isFetchingEntities;

  const projectionQuery = useQuery<XdaProjectionResponse, AxiosError>(
    [GetXdaProjectionKey, txnId, xdaDimensions.leftWidth, xdaDimensions.rightWidth],
    () => {
      const request: XdaProjectionRequest = {
        uwis,
        rightLateralDistance: xdaDimensions.rightWidth,
        leftLateralDistance: xdaDimensions.leftWidth,
        colorMap
      };

      return fetchXdaProjection(request);
    },
    {
      refetchOnWindowFocus: false,
      enabled: shouldFetch,
      placeholderData: emptyXdaProjectionResponse,
      staleTime: 0,
      cacheTime: MAP_QUERY_CACHE_TIME,
      keepPreviousData: true
    }
  );

  // Use isFetching as well since isLoading is only true on initial load without cached data
  const isLoadingOrFetching =
    isLoadingEntities ||
    isFetchingEntities ||
    projectionQuery.isLoading ||
    projectionQuery.isFetching;

  return {
    ...projectionQuery,
    isLoading: isLoadingOrFetching,
    dataUpdatedAt: projectionQuery.dataUpdatedAt
  };
};

export default useGetXdaProjection;
